import { useState } from "react";
import { observer } from "mobx-react-lite";
import Icon from "@mdi/react";
import {
  mdiCheck,
  mdiAlertCircleOutline,
  mdiCreditCardOutline,
  mdiAccountOutline,
  mdiClockOutline,
  mdiContentCopy,
  mdiOpenInNew,
} from "@mdi/js";
import { Modal, Tabs, Chip, cn } from "@heroui/react";
import { clientDetailStore } from "@/stores/ClientDetailStore";
import type { PaymentDetailSubStore as ClientPdStore } from "@/stores/ClientDetailStore/PaymentDetailSubStore";
import type { PaymentDetailSubStore as ContractorPdStore } from "@/stores/ContractorDetailStore/PaymentDetailSubStore";
import "./DetailModal.css";
import {
  PAYMENT_METHOD_LABEL,
  BOOKING_STATUS_LABEL,
  BOOKING_STATUS_COLOR,
  PAYMENT_STATUS_LABEL,
  PAYMENT_STATUS_COLOR,
  TRANSACTION_STATUS_LABEL,
  TRANSACTION_STATUS_COLOR,
  TRANSACTION_TYPE_LABEL,
  TRANSACTION_TYPE_COLOR,
} from "../../../pages/ContractorDetailPage/constants";
import { formatMoneyRub } from "@shared-utils/helpers";

const RECEIPT_TYPE_LABEL: Record<string, string> = {
  Income: "Приход",
  IncomeReturn: "Возврат прихода",
  income: "Приход",
  refund: "Возврат",
  income_return: "Возврат прихода",
  expense: "Расход",
};

const RECEIPT_STATUS_LABEL: Record<string, string> = {
  Queued: "Ожидание",
  Processed: "Выполнен",
  Error: "Ошибка",
  NotFound: "Не найден",
  done: "Выполнен",
  pending: "Ожидание",
  failed: "Ошибка",
  wait: "Ожидание",
  printed: "Напечатан",
};

const STATUS_BANNER: Record<
  string,
  { className: string; icon: string; title: string; subtitle: string }
> = {
  success: {
    className: "scd-status-banner--success",
    icon: mdiCheck,
    title: "Проведён",
    subtitle: "Средства списаны",
  },
  failed: {
    className: "scd-status-banner--danger",
    icon: mdiAlertCircleOutline,
    title: "Ошибка",
    subtitle: "Оплата не прошла",
  },
  pending: {
    className: "scd-status-banner--warning",
    icon: mdiClockOutline,
    title: "Ожидание",
    subtitle: "Ожидание подтверждения",
  },
  pending_confirmation: {
    className: "scd-status-banner--warning",
    icon: mdiClockOutline,
    title: "Ожид. подтвержд.",
    subtitle: "Требуется подтверждение",
  },
  processing: {
    className: "scd-status-banner--info",
    icon: mdiClockOutline,
    title: "Обработка",
    subtitle: "Платёж обрабатывается",
  },
  refunded: {
    className: "scd-status-banner--neutral",
    icon: mdiCheck,
    title: "Возвращён",
    subtitle: "Средства возвращены",
  },
  partially_refunded: {
    className: "scd-status-banner--neutral",
    icon: mdiCheck,
    title: "Частичн. возврат",
    subtitle: "Часть средств возвращена",
  },
};

interface TransactionDetailModalProps {
  pd?: ClientPdStore | ContractorPdStore;
}

const TransactionDetailModal: React.FC<TransactionDetailModalProps> = observer(
  ({ pd: pdProp }) => {
    const pd = pdProp ?? clientDetailStore.pd;
    const [copiedKey, setCopiedKey] = useState<string | null>(null);
    const {
      isOpen,
      detail: paymentDetail,
      events: paymentEvents,
      isLoading: isPaymentDetailLoading,
      description,
      transactionType,
      defaultTab,
    } = pd;

    if (!isOpen) return null;

    const payment = paymentDetail?.payment;
    const receipt = paymentDetail?.receipt;
    const booking = paymentDetail?.booking;
    const tbankState = paymentDetail?.tbankState;

    const title =
      description ||
      (payment
        ? `Платёж #${payment.tbankPaymentId || payment.uuid.slice(0, 8)}`
        : "...");

    const banner = payment ? STATUS_BANNER[payment.status] : null;

    const handleCopy = (key: string, value: string) => {
      navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    };

    const fmtDate = (iso: string) =>
      new Date(iso).toLocaleString("ru-RU", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });

    return (
      <Modal.Backdrop isOpen={isOpen} onOpenChange={() => pd.close()}>
        <Modal.Container className="scd-tx-modal-container">
          <Modal.Dialog className="scd-tx-modal">
            <Modal.CloseTrigger />

            {isPaymentDetailLoading || !payment ? (
              <Modal.Body
                className="scd-tx-modal__body"
                style={{ padding: "40px", textAlign: "center" }}
              >
                Загрузка...
              </Modal.Body>
            ) : (
              <>
                <Modal.Header className="scd-tx-modal__header">
                  <div className="scd-tx-modal__title-row">
                    <Modal.Heading>{title}</Modal.Heading>
                    <span
                      className={cn(
                        "scd-tx-modal__amount",
                        payment.amount >= 0
                          ? "scd-amount--positive"
                          : "scd-amount--negative",
                      )}
                    >
                      {payment.amount >= 0 ? "+" : ""}
                      {formatMoneyRub(payment.amount)}
                    </span>
                  </div>
                  <div className="scd-tx-modal__chips">
                    {transactionType && (
                      <Chip
                        color={
                          TRANSACTION_TYPE_COLOR[transactionType] ?? "default"
                        }
                        variant="soft"
                        size="sm"
                      >
                        {TRANSACTION_TYPE_LABEL[transactionType] ??
                          transactionType}
                      </Chip>
                    )}
                    <Chip
                      color={
                        TRANSACTION_STATUS_COLOR[payment.status] ?? "default"
                      }
                      variant="soft"
                      size="sm"
                    >
                      {TRANSACTION_STATUS_LABEL[payment.status] ??
                        payment.status}
                    </Chip>
                    <span className="scd-tx-modal__date">
                      {fmtDate(payment.createdAt)}
                    </span>
                  </div>
                </Modal.Header>

                <Modal.Body className="scd-tx-modal__body">
                  <Tabs
                    className="scd-tx-modal__tabs"
                    defaultSelectedKey={defaultTab ?? "overview"}
                  >
                    <Tabs.ListContainer>
                      <Tabs.List
                        className="scd-tx-modal__tabs-list"
                        aria-label="Разделы транзакции"
                      >
                        <Tabs.Tab id="overview">
                          Обзор
                          <Tabs.Indicator />
                        </Tabs.Tab>
                        <Tabs.Tab id="timeline">
                          История
                          <Tabs.Indicator />
                        </Tabs.Tab>
                        {receipt && (
                          <Tabs.Tab id="receipt">
                            Чек
                            <Tabs.Indicator />
                          </Tabs.Tab>
                        )}
                        {booking && (
                          <Tabs.Tab id="booking">
                            Бронирование
                            <Tabs.Indicator />
                          </Tabs.Tab>
                        )}
                      </Tabs.List>
                    </Tabs.ListContainer>

                    {/* ── Обзор ── */}
                    <Tabs.Panel id="overview" className="scd-tx-modal__panel">
                      {banner && (
                        <div
                          className={cn("scd-status-banner", banner.className)}
                        >
                          <Icon path={banner.icon} size={0.8} />
                          <div className="scd-status-banner__text">
                            <span className="scd-status-banner__label">
                              {banner.title}
                            </span>
                            <span className="scd-status-banner__sub">
                              {banner.subtitle}
                            </span>
                          </div>
                        </div>
                      )}

                      <div className="scd-detail-table">
                        {description && (
                          <div className="scd-detail-table__row">
                            <span className="scd-detail-table__label">
                              Описание
                            </span>
                            <span className="scd-detail-table__value">
                              {description}
                            </span>
                          </div>
                        )}
                        <div className="scd-detail-table__row">
                          <span className="scd-detail-table__label">Сумма</span>
                          <span
                            className={cn(
                              "scd-detail-table__value scd-detail-table__value--bold",
                              payment.amount >= 0
                                ? "scd-amount--positive"
                                : "scd-amount--negative",
                            )}
                          >
                            {payment.amount >= 0 ? "+" : ""}
                            {formatMoneyRub(payment.amount)}
                          </span>
                        </div>
                        <div className="scd-detail-table__row">
                          <span className="scd-detail-table__label">
                            Дата и время
                          </span>
                          <span className="scd-detail-table__value">
                            {fmtDate(payment.createdAt)}
                          </span>
                        </div>
                        {transactionType && (
                          <div className="scd-detail-table__row">
                            <span className="scd-detail-table__label">
                              Тип операции
                            </span>
                            <span className="scd-detail-table__value">
                              <Chip
                                color={
                                  TRANSACTION_TYPE_COLOR[transactionType] ??
                                  "default"
                                }
                                variant="soft"
                                size="sm"
                              >
                                {TRANSACTION_TYPE_LABEL[transactionType] ??
                                  transactionType}
                              </Chip>
                            </span>
                          </div>
                        )}
                        <div className="scd-detail-table__row">
                          <span className="scd-detail-table__label">
                            Метод оплаты
                          </span>
                          <span className="scd-detail-table__value">
                            <Icon
                              path={mdiCreditCardOutline}
                              size={0.55}
                              className="scd-detail-table__icon"
                            />
                            {PAYMENT_METHOD_LABEL[payment.paymentMethod] ??
                              payment.paymentMethod}
                          </span>
                        </div>
                        {tbankState && (
                          <div className="scd-detail-table__row">
                            <span className="scd-detail-table__label">
                              Статус Tbank
                            </span>
                            <span className="scd-detail-table__value">
                              <Chip variant="soft" size="sm">
                                {tbankState.status}
                              </Chip>
                            </span>
                          </div>
                        )}
                        <div className="scd-detail-table__row">
                          <span className="scd-detail-table__label">
                            Наш статус
                          </span>
                          <span className="scd-detail-table__value">
                            <Chip
                              color={
                                TRANSACTION_STATUS_COLOR[payment.status] ??
                                "default"
                              }
                              variant="soft"
                              size="sm"
                            >
                              {payment.status}
                            </Chip>
                          </span>
                        </div>
                        {payment.tbankPaymentId && (
                          <div className="scd-detail-table__row">
                            <span className="scd-detail-table__label">
                              T-Bank Payment ID
                            </span>
                            <span className="scd-detail-table__value">
                              {payment.tbankPaymentId}
                            </span>
                          </div>
                        )}
                        {payment.tbankOrderId && (
                          <div className="scd-detail-table__row">
                            <span className="scd-detail-table__label">
                              T-Bank Order ID
                            </span>
                            <span className="scd-detail-table__value">
                              {payment.tbankOrderId}
                            </span>
                          </div>
                        )}
                        {payment.errorMessage && (
                          <div className="scd-detail-table__row">
                            <span className="scd-detail-table__label">
                              Ошибка
                            </span>
                            <span className="scd-detail-table__value scd-detail-table__value--error">
                              {payment.errorMessage}
                            </span>
                          </div>
                        )}
                      </div>
                    </Tabs.Panel>

                    {/* ── История ── */}
                    <Tabs.Panel id="timeline" className="scd-tx-modal__panel">
                      {paymentEvents.length === 0 ? (
                        <p
                          style={{
                            color: "var(--color-gray-500)",
                            textAlign: "center",
                          }}
                        >
                          Нет событий
                        </p>
                      ) : (
                        <div className="scd-event-table">
                          <div className="scd-event-table__head">
                            <span>Время</span>
                            <span>Tbank</span>
                            <span>Статус</span>
                            <span>Описание</span>
                            <span>Источник</span>
                          </div>
                          {paymentEvents.map((ev) => {
                            const time = new Date(
                              ev.createdAt,
                            ).toLocaleTimeString("ru-RU", {
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                            });
                            return (
                              <div
                                key={ev.uuid}
                                className={cn(
                                  "scd-event-table__row",
                                  !ev.success && "scd-event-table__row--error",
                                )}
                              >
                                <span className="scd-event-table__time">
                                  {time}
                                </span>
                                <span>
                                  {ev.tbankStatus ? (
                                    <Chip
                                      variant="soft"
                                      size="sm"
                                      color={ev.success ? "success" : "warning"}
                                    >
                                      {ev.tbankStatus}
                                    </Chip>
                                  ) : (
                                    "—"
                                  )}
                                </span>
                                <span>
                                  <Chip
                                    variant="soft"
                                    size="sm"
                                    color={ev.success ? "success" : "danger"}
                                  >
                                    {ev.ourStatus}
                                  </Chip>
                                </span>
                                <span className="scd-event-table__desc">
                                  {ev.statusDisplay}
                                </span>
                                <span className="scd-event-table__source">
                                  {ev.eventSource ?? "—"}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </Tabs.Panel>

                    {/* ── Чек ── */}
                    {receipt && (
                      <Tabs.Panel id="receipt" className="scd-tx-modal__panel">
                        <div className="scd-detail-table">
                          {receipt.receiptUrl && (
                            <div className="scd-detail-table__row">
                              <span className="scd-detail-table__label">
                                Ссылка на чек
                              </span>
                              <span className="scd-detail-table__value">
                                <a
                                  href={receipt.receiptUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="scd-receipt-link"
                                >
                                  <Icon path={mdiOpenInNew} size={0.55} />
                                  Открыть чек
                                </a>
                              </span>
                            </div>
                          )}
                          <div className="scd-detail-table__row">
                            <span className="scd-detail-table__label">Тип</span>
                            <span className="scd-detail-table__value">
                              <Chip color="default" variant="soft" size="sm">
                                {RECEIPT_TYPE_LABEL[receipt.type] ??
                                  receipt.type}
                              </Chip>
                            </span>
                          </div>
                          <div className="scd-detail-table__row">
                            <span className="scd-detail-table__label">
                              Статус
                            </span>
                            <span className="scd-detail-table__value">
                              <Chip
                                color={
                                  receipt.status === "Processed"
                                    ? "success"
                                    : receipt.status === "Error"
                                      ? "danger"
                                      : receipt.status === "Queued"
                                        ? "warning"
                                        : "default"
                                }
                                variant="soft"
                                size="sm"
                              >
                                {RECEIPT_STATUS_LABEL[receipt.status] ??
                                  receipt.status}
                              </Chip>
                            </span>
                          </div>
                          {(
                            [
                              receipt.fiscalSign != null && {
                                key: "fiscalSign",
                                label: "Фискальный признак",
                                value: String(receipt.fiscalSign),
                              },
                              receipt.fiscalNumber != null && {
                                key: "fiscalNumber",
                                label: "№ фискального документа",
                                value: String(receipt.fiscalNumber),
                              },
                              receipt.inn != null && {
                                key: "inn",
                                label: "ИНН",
                                value: String(receipt.inn),
                              },
                            ].filter(Boolean) as {
                              key: string;
                              label: string;
                              value: string;
                            }[]
                          ).map(({ key, label, value }) => (
                            <div key={key} className="scd-detail-table__row">
                              <span className="scd-detail-table__label">
                                {label}
                              </span>
                              <span className="scd-detail-table__value">
                                {value}
                                <button
                                  className={cn(
                                    "scd-copy-btn",
                                    copiedKey === key && "scd-copy-btn--copied",
                                  )}
                                  onClick={() => handleCopy(key, value)}
                                  title="Скопировать"
                                >
                                  <Icon path={mdiContentCopy} size={0.5} />
                                  {copiedKey === key && (
                                    <span className="scd-copy-btn__tooltip">
                                      Скопировано
                                    </span>
                                  )}
                                </button>
                              </span>
                            </div>
                          ))}
                          <div className="scd-detail-table__divider" />
                          <div className="scd-detail-table__row scd-detail-table__row--total">
                            <span className="scd-detail-table__label">
                              Сумма
                            </span>
                            <span className="scd-detail-table__value scd-detail-table__value--bold">
                              {formatMoneyRub(receipt.amount)}
                            </span>
                          </div>
                          {receipt.createdAt && (
                            <div className="scd-detail-table__row">
                              <span className="scd-detail-table__label">
                                Дата создания
                              </span>
                              <span className="scd-detail-table__value">
                                {new Date(receipt.createdAt).toLocaleString(
                                  "ru-RU",
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </Tabs.Panel>
                    )}

                    {/* ── Бронирование ── */}
                    {booking && (
                      <Tabs.Panel id="booking" className="scd-tx-modal__panel">
                        <div className="scd-detail-table">
                          <div className="scd-detail-table__row">
                            <span className="scd-detail-table__label">
                              Клиент
                            </span>
                            <span className="scd-detail-table__value">
                              <Icon
                                path={mdiAccountOutline}
                                size={0.55}
                                className="scd-detail-table__icon"
                              />
                              {booking.client.name}
                            </span>
                          </div>
                          <div className="scd-detail-table__row">
                            <span className="scd-detail-table__label">
                              Телефон
                            </span>
                            <span className="scd-detail-table__value">
                              {booking.client.phone}
                            </span>
                          </div>
                          <div className="scd-detail-table__row">
                            <span className="scd-detail-table__label">
                              Услуга
                            </span>
                            <span className="scd-detail-table__value">
                              {booking.service.name}
                            </span>
                          </div>
                          <div className="scd-detail-table__row">
                            <span className="scd-detail-table__label">
                              Автомобиль
                            </span>
                            <span className="scd-detail-table__value">
                              {booking.car?.make} {booking.car?.model}
                            </span>
                          </div>
                          <div className="scd-detail-table__row">
                            <span className="scd-detail-table__label">
                              Дата / время
                            </span>
                            <span className="scd-detail-table__value">
                              {new Date(booking.startTime).toLocaleString(
                                "ru-RU",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}
                              –
                              {new Date(booking.endTime).toLocaleTimeString(
                                "ru-RU",
                                { hour: "2-digit", minute: "2-digit" },
                              )}
                            </span>
                          </div>
                          <div className="scd-detail-table__row">
                            <span className="scd-detail-table__label">
                              Статус
                            </span>
                            <span className="scd-detail-table__value">
                              <Chip
                                color={
                                  BOOKING_STATUS_COLOR[booking.status] ??
                                  "default"
                                }
                                variant="soft"
                                size="sm"
                              >
                                {BOOKING_STATUS_LABEL[booking.status] ??
                                  booking.status}
                              </Chip>
                            </span>
                          </div>
                          <div className="scd-detail-table__row">
                            <span className="scd-detail-table__label">
                              Оплата
                            </span>
                            <span className="scd-detail-table__value">
                              <Chip
                                color={
                                  PAYMENT_STATUS_COLOR[
                                    (booking.paymentStatus as string | null) ??
                                      ""
                                  ] ?? "default"
                                }
                                variant="soft"
                                size="sm"
                              >
                                {PAYMENT_STATUS_LABEL[
                                  (booking.paymentStatus as string | null) ?? ""
                                ] ?? (booking.paymentStatus as string | null)}
                              </Chip>
                            </span>
                          </div>
                          <div className="scd-detail-table__row">
                            <span className="scd-detail-table__label">
                              Стоимость
                            </span>
                            <span className="scd-detail-table__value scd-detail-table__value--bold">
                              {formatMoneyRub(booking.totalCost)}
                            </span>
                          </div>
                        </div>
                      </Tabs.Panel>
                    )}
                  </Tabs>
                </Modal.Body>
              </>
            )}
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    );
  },
);

TransactionDetailModal.displayName = "TransactionDetailModal";
export default TransactionDetailModal;
